/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */


'use strict';

import {Dictionary} from "../utils/Dictionary";
import {CategoryEntity} from "../entities/category";
import {recordTypes} from './recordTypes';
import {createEntityLinker, noop} from "../utils/tools";
import {EntityStates, EntityTypes} from "../utils/dvtypes";

/**
 * @callback MergeCategoryEntityCallback
 * @param {CategoryEntity} categoryEntity - The Category Entity that was merged
 * @param {string} actionString - The action that was taken with the merged entity
 */

class Categories {
    constructor () {
        /**
         * Dictionary object for all category entities
         * @type {Dictionary}
         */
        this.allCategories = new Dictionary();
    }

    /**
     * Merge new or updated category entities with the category cache
     * @param {Array<Object>} entities - Array of entity objects that will become CategoryEntity objects
     * @param {MergeCategoryEntityCallback} [newCallback = noop]
     * @param {MergeCategoryEntityCallback} [updatedCallback = noop]
     */
    mergeCache (entities, newCallback = noop, updatedCallback = noop) {
        if (!Array.isArray(entities)) {
            return;
        }

        entities.forEach(
            /**
             * Loop through all raw JSON category entity objects passed to us
             * @param {Object} entity
             */
            (entity) => {
                let category = new CategoryEntity(entity),
                    cachedCategory = this.allCategories.getVal(category.CategoryID);

                if (!cachedCategory) {
                    // update the state to unmodified - we are getting this from the service, after all...
                    category.setState(EntityStates.Unmodified);

                    // this is a new property, add it to the cache
                    this.allCategories.add(category.CategoryID, category, category.UID);

                    // Update parent RecordType childCategories
                    let parentRT = recordTypes.allRecordTypes.getVal({ UID: category.RecordTypeUID });
                    if (parentRT) {
                        if (!Array.isArray(parentRT.clientProperties.childProperties)) {
                            parentRT.clientProperties.childCategories = [];
                        }

                        // add the category to the parent record type entity
                        parentRT.clientProperties.childCategories.push(category);

                        // ensure that the category has a reference to the parent record type entity
                        category.clientProperties.Parent = parentRT;
                    }
                    else {
                        console.error(`Missing parent record type [${category.CategoryUID}] from property [${category.UID}]`);
                    }

                    newCallback(category, 'added');
                }
                else {
                    if (category.State === EntityStates.Deleted) {
                        let parentRT = recordTypes.allRecordTypes.getVal(createEntityLinker(EntityTypes.RecordType, category.RecordTypeUID));
                        if (parentRT) {
                            let children = parentRT.clientProperties.childProperties,
                                cat = children.find(p => p.CategoryID === category.CategoryID),
                                index = children.indexOf(cat);
                            if (cat) {
                                children.splice(index, 1);
                            }
                        }

                        // remove the deleted entity
                        this.allCategories.remove(category.CategoryID);

                        updatedCallback(category, 'removed');
                    }
                    else {
                        // update the existing category
                        cachedCategory.Definition = category.Definition;
                        cachedCategory.Name = category.Name;

                        updatedCallback(category, 'updated');
                    }
                }
            });
    }

    /**
     * Get all category entities for the specified record type UID
     * @param {string} rtUID - UID string of the record type to find categories for
     * @returns {Array<CategoryEntity>} - Array of category entity objects for the specified record type
     */
    getAllCategoriesForRecordType (rtUID) {
        return this.allCategories.filter(c => c.RecordTypeUID === rtUID).Values;
    }
}

export const categories = new Categories();
